<script setup>
import { defineProps, computed, reactive, ref } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());
const fetchingReplyMessageIds = reactive(new Set());
const replyMessageVersion = ref(0);

const normalizeMessageId = id => {
  const normalizedId = Number(id);
  return Number.isNaN(normalizedId) ? id : normalizedId;
};

const matchesMessageId = (messageId, targetId) =>
  normalizeMessageId(messageId) === normalizeMessageId(targetId);

const findMessageById = (messages, messageId) => {
  if (!messages?.length || messageId == null) return null;

  return (
    messages.find(message => matchesMessageId(message.id, messageId)) || null
  );
};

const findMessageBySourceId = (messages, sourceId) => {
  if (!messages?.length || !sourceId) return null;

  return (
    messages.find(
      message => message.source_id === sourceId || message.sourceId === sourceId
    ) || null
  );
};

const formatReplyMessage = replyMessage => {
  if (!replyMessage) return null;

  if (
    replyMessage.messageType !== undefined ||
    replyMessage.message_type !== undefined
  ) {
    return replyMessage.messageType !== undefined
      ? replyMessage
      : useCamelCase(replyMessage, { deep: true });
  }

  return useCamelCase(replyMessage, { deep: true });
};

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  const normalizedId = normalizeMessageId(messageId);

  if (fetchedReplyMessages.has(normalizedId)) {
    return fetchedReplyMessages.get(normalizedId);
  }

  if (fetchingReplyMessageIds.has(normalizedId)) {
    return undefined;
  }

  fetchingReplyMessageIds.add(normalizedId);

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: normalizedId + 100,
      after: Math.max(0, normalizedId - 100),
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg =>
      matchesMessageId(msg.id, normalizedId)
    );

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage, { deep: true });
      fetchedReplyMessages.set(normalizedId, camelCaseMessage);
      return camelCaseMessage;
    }

    fetchedReplyMessages.set(normalizedId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(normalizedId, null);
    return null;
  } finally {
    fetchingReplyMessageIds.delete(normalizedId);
    replyMessageVersion.value += 1;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  // Track cache updates so async fetches re-render reply previews.
  void replyMessageVersion.value;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;
  const inReplyToExternalId =
    parentMessage.contentAttributes?.inReplyToExternalId ??
    parentMessage.content_attributes?.in_reply_to_external_id;

  if (!inReplyToMessageId && !inReplyToExternalId) return null;

  let replyMessage = findMessageById(props.messages, inReplyToMessageId);

  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = findMessageById(
      currentChat.value.messages,
      inReplyToMessageId
    );
  }

  if (!replyMessage && inReplyToExternalId) {
    replyMessage =
      findMessageBySourceId(props.messages, inReplyToExternalId) ||
      findMessageBySourceId(currentChat.value?.messages, inReplyToExternalId);
  }

  if (!replyMessage && inReplyToMessageId != null) {
    const normalizedId = normalizeMessageId(inReplyToMessageId);

    if (fetchedReplyMessages.has(normalizedId)) {
      replyMessage = fetchedReplyMessages.get(normalizedId);

      if (!replyMessage) {
        return { id: normalizedId };
      }
    } else if (currentChat.value?.id) {
      fetchReplyMessage(normalizedId, currentChat.value.id);

      if (fetchingReplyMessageIds.has(normalizedId)) {
        return { id: normalizedId, _loading: true };
      }
    }
  }

  return formatReplyMessage(replyMessage);
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
